import { AgPrefill } from '../../../../app/api/registrationApi/types'
import {
    DisplayValues,
    FormValues,
} from '../../../Store/reducers/formReducer/types'

// DEV-3255: AG会員の転記を「適用する / 取り消す」計画を組み立てる。
//
// 転記の肝は、値とステップの完了状態を必ず対で動かすこと。片方だけ動かすと
// 「値は空なのに回答済み」の状態ができ、そのステップが飛ばされて空のまま登録へ進む。
// EmailOnlyから切り出しているのは、この対応を単体テストで固定するため。

// 転記が値を入れるステップのindex（Bパターンの SIGNUP_STEPS_B における位置）。
// fullName はスキップせず、転記値をグレーアウト表示して本人に確認させる（Figma 2970）。
// Steps配列を並び替えたときはここも直す。Jestで対応するComponentを固定している
export const B_PREFILL_STEP_INDICES = {
    fullName: 2,
    tel: 5,
    workingStatus: 7,
    experienceYears: 8,
    enrollmentPeriod: 11,
} as const

// ステップのindex → そのステップが持つフォーム項目。
// 氏名ステップはカナも持つ。転記するのは姓名だけだが、カナを道連れにしないと
// 「姓名はAG会員・カナは前に入れた別の氏名」という組み合わせが残る
// （FullName/validate.ts は姓名とカナを相互検証しないので、そのまま登録できてしまう）。
// 電話番号はブラウザに値を渡さない（登録時にサーバが引き直して補う）ので項目が無い
const FORM_KEYS_BY_STEP: Record<number, (keyof FormValues)[]> = {
    [B_PREFILL_STEP_INDICES.fullName]: [
        'lastName',
        'firstName',
        'lastNameKana',
        'firstNameKana',
    ],
    [B_PREFILL_STEP_INDICES.workingStatus]: ['situation'],
    [B_PREFILL_STEP_INDICES.experienceYears]: ['experienceYears'],
}

// 飛ばしたステップが確認画面に出す表示用の値。飛ばすと「次へ」を通らないため、
// ここで入れないと確認画面が項目名だけの行になり、戻って直すこともできない。
// 経験年数は確認画面に項目が無いので対象外
const DISPLAY_KEYS_BY_STEP: Record<number, keyof DisplayValues> = {
    [B_PREFILL_STEP_INDICES.tel]: 'tel',
    [B_PREFILL_STEP_INDICES.workingStatus]: 'situation',
}

// 就業状況の表示用の値。WorkingStatus の displayFormValue と同じ文字列にする
const SITUATION_DISPLAY_VALUES: Record<string, string> = {
    tenure: '在職中',
    resignation: '離職中',
}

type PrefillPlan = {
    /** フォームへ入れる値 */
    values: [keyof FormValues, string][]
    /** 確認画面に出す表示用の値 */
    displayValues: [keyof DisplayValues, string][]
    /** 値が埋まったので飛ばすステップ。氏名は転記しても表示するので含まない */
    skippedSteps: number[]
    /** 転記値を見せるために、回答済みでも未回答へ戻すステップ */
    reopenSteps: number[]
}

type DiscardPlan = {
    /** 空に戻すフォーム項目 */
    clearKeys: (keyof FormValues)[]
    /** 空に戻す表示用の値 */
    clearDisplayKeys: (keyof DisplayValues)[]
    /** 未回答に戻すステップ */
    resetSteps: number[]
}

/**
 * @param prefill 照合APIが返したAG会員の登録内容
 * @param answered このフォームで本人が既に回答している値。転記で上書きしないために渡す
 * @param email 照合したメールアドレス
 * @returns フォームへ入れる値と、飛ばす／未回答へ戻すステップ
 */
export const planPrefill = (
    prefill: AgPrefill,
    answered: { experienceYears: string; tel: string },
    email: string
): PrefillPlan => {
    const values: [keyof FormValues, string][] = [
        // 転記が効いている印。氏名のロック（FullName）と、メールアドレスを
        // 変えたときの取り消しの判定に使う
        ['agPrefillAppliedEmail', email],
        ['lastName', prefill.last_name],
        ['firstName', prefill.first_name],
        // カナは転記せずユーザーに入力させる（Figma 2970）。前の氏名のカナが
        // 残ったままにならないよう空に戻す
        ['lastNameKana', ''],
        ['firstNameKana', ''],
    ]
    const displayValues: [keyof DisplayValues, string][] = []
    const skippedSteps: number[] = []
    // 氏名はスキップせず転記値を見せる仕様なので、既に回答済みでも未回答へ戻す。
    // 戻さないと、先に自分の氏名を入力してからメールをAG会員のものに変えたときに、
    // 本人が一度も見ない画面で姓名だけAG値に差し替わったまま登録される
    const reopenSteps: number[] = [B_PREFILL_STEP_INDICES.fullName]

    // 伏せ字が無いと確認画面が項目名だけの行になり、飛ばした先は本人が直せないため、
    // 表示値を出せないときはステップを残して自分で入力させる。
    // 本人が既に入力しているときも飛ばさない。サーバが補うのはフォームが空のときだけなので、
    // 飛ばすと「確認画面は伏せ字・登録されるのは手入力の番号」という食い違いになる
    if (prefill.skip_tel && prefill.masked_tel && !answered.tel) {
        skippedSteps.push(B_PREFILL_STEP_INDICES.tel)
        displayValues.push(['tel', prefill.masked_tel])
    }

    // 就業状況は転記してステップを飛ばす。
    // NOTE: AG側の登録フォームのうちレガシー（LINE友だち追加後のフォーム等）は
    // 「在職中」が初期選択済みのまま送信できるため、届いた在職中が本人の回答か
    // デフォルトのままかを区別できない（判別用のフラグも無い）。飛ばすと本人は訂正
    // できないが、DEV-3255 とステップ構成を揃えて比較するため同じ挙動にしている
    if (prefill.situation) {
        values.push(['situation', prefill.situation])
        skippedSteps.push(B_PREFILL_STEP_INDICES.workingStatus)
        const situationDisplayValue =
            SITUATION_DISPLAY_VALUES[prefill.situation]
        if (situationDisplayValue) {
            displayValues.push(['situation', situationDisplayValue])
        }

        // 離職中は勤務終了日が要る。通常フローでは就業状況で「離職中」を選ぶと
        // ChoicesButton が在籍期間を未回答へ戻すので、転記でも同じ連動をさせる
        if (prefill.situation === 'resignation') {
            reopenSteps.push(B_PREFILL_STEP_INDICES.enrollmentPeriod)
        }
    }

    // 対応表に無い経験年数はnullで返る。その項目だけ転記せずユーザーに選ばせる（部分転記）。
    // 本人が既に選んでいるときも転記しない。経験年数はステップを飛ばすため、上書きすると
    // 本人が一度も見ないまま差し替わり、確認画面にも出ないので気づく手段が無い
    if (prefill.experience_years && !answered.experienceYears) {
        values.push(['experienceYears', prefill.experience_years])
        skippedSteps.push(B_PREFILL_STEP_INDICES.experienceYears)
    }

    return { values, displayValues, skippedSteps, reopenSteps }
}

/**
 * @param skippedSteps 転記で実際に飛ばしたステップ（= 転記した値で埋めたステップ）
 * @returns 空に戻す項目と、未回答へ戻すステップ
 */
export const planDiscard = (skippedSteps: number[]): DiscardPlan => {
    // 氏名はスキップせず表示するのでskippedStepsに入らないが、値は転記しているので
    // ここで必ず戻す。戻し忘れると、前の人の値が入ったまま回答済み扱いになる
    const resetSteps = [B_PREFILL_STEP_INDICES.fullName, ...skippedSteps]

    return {
        // 転記が効いている印はどのステップにも属さないが、
        // 転記の有無そのものを表すので必ず戻す
        clearKeys: [
            'agPrefillAppliedEmail',
            ...resetSteps.flatMap(index => FORM_KEYS_BY_STEP[index] ?? []),
        ],
        // 転記で入れた表示用の値も戻す。残すと、メールを変えて転記が外れたのに
        // 確認画面にだけ前の人の就業状況・電話番号が出たままになる。
        // 実際に飛ばしたステップの分だけ消す。飛ばしていないステップの表示値は
        // 本人が自分で入力したものなので消さない
        clearDisplayKeys: skippedSteps.flatMap(
            index => DISPLAY_KEYS_BY_STEP[index] ?? []
        ),
        resetSteps,
    }
}
